import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { APIProviderContext } from "../api";
import { PulseLoader } from "react-spinners";
import { useSearchParams } from "react-router-dom";
import { WhiteLabelContext } from "../whitelabel";
import { AppsumoIcon } from "../../assets/ui-icons";
import Modal from "./dashboardModal";
import { AccountContext } from "../account/account";
import { Trans } from "@lingui/react/macro";
import { BannerContainer } from "../banner";

const GrigoraAccountContext = createContext();

var enterpriseDomains = ["@grigora.co", "@latracal.com"];

const GrigoraAccount = (props) => {
  const { userAttributes } = useContext(AccountContext) ?? {};

  const [searchParams, setSearchParams] = useSearchParams();
  const [isAppsumoModalVisible, setIsAppsumoModalVisible] = useState(false);

  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const { isWhitelabel, whitelabelPlans, isWhiteLabelOwner } =
    useContext(WhiteLabelContext) ?? {};

  const { getUserData } = useContext(APIProviderContext);

  const [isSaasZillaCampaignVisible, setIsSaasZillaCampaignVisible] =
    useState(false);

  useEffect(() => {
    setIsLoading(true);

    getUserData()
      .then((data) => {
        setUserData(data);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (searchParams?.get("appsumo") === "connected") {
      setSearchParams({});
      setIsAppsumoModalVisible(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!isLoading) {
    }
  }, [isLoading]);

  const isSubscribed = useMemo(() => {
    if (enterpriseDomains.some((domain) => userData?.email?.endsWith(domain))) {
      return true;
    }

    return userData?.subscription?.status === "active";
  }, [userData?.subscription?.status]);

  const subscriptionPlan = useMemo(() => {
    if (enterpriseDomains.some((domain) => userData?.email?.endsWith(domain))) {
      return "enterprise";
    }

    return userData?.subscription?.plan ?? "free";
  }, [userData?.subscription?.plan]);

  const subscriptionLimits = useMemo(() => {
    if (!isSubscribed) {
      return userData?.subscriptionLimits;
    }

    if (isWhitelabel && !isWhiteLabelOwner) {
      return (
        whitelabelPlans?.[subscriptionPlan]?.limits ??
        userData?.subscriptionLimits
      );
    }

    return userData?.subscriptionLimits;
  }, [
    userData,
    isSubscribed,
    subscriptionPlan,
    whitelabelPlans,
    isWhitelabel,
    isWhiteLabelOwner,
  ]);

  const subscriptionAddons = useMemo(() => {
    if (!isSubscribed) {
      return userData?.subscriptionAddons;
    }

    if (isWhitelabel && !isWhiteLabelOwner) {
      return [];
    }

    return userData?.subscriptionAddons;
  }, [
    userData,
    isSubscribed,
    subscriptionPlan,
    whitelabelPlans,
    isWhitelabel,
    isWhiteLabelOwner,
  ]);

  const whitelabelPurchaseShouldDisplay = useMemo(() => {
    if (!isSubscribed) {
      return false;
    }

    if (isWhitelabel && !isWhiteLabelOwner) {
      return false;
    }

    if (subscriptionLimits?.whitelabel) {
      return false;
    }

    return true;
  }, [
    userData,
    isSubscribed,
    subscriptionPlan,
    subscriptionLimits,
    whitelabelPlans,
    isWhitelabel,
    isWhiteLabelOwner,
  ]);

  const ALLOWED_USERS = ["pratik@latracal.com", "patilsohanp@gmail.com"];

  function isDev() {
    if (
      process.env.REACT_APP_GRIGORA_DEBUG_EDITOR ||
      (ALLOWED_USERS.includes(userAttributes?.email) && userAttributes?.email)
    ) {
      return true;
    }

    return false;
  }

  const AI_ACCESS_ALLOWED = [
    "pratik@latracal.com",
    "patilsohanp@gmail.com",
    "karan@latracal.com",
  ];

  function isAIAllowed() {
    return true;
    // if (
    //   process.env.REACT_APP_GRIGORA_DEBUG_EDITOR ||
    //   (AI_ACCESS_ALLOWED.includes(userAttributes?.email) &&
    //     userAttributes?.email)
    // ) {
    //   return true;
    // }

    // if (userData?.betaSignups?.["ai-site-editor"]) {
    //   return true;
    // }

    // return false;
  }

  function isMailAllowed() {
    return true;
  }

  return (
    <GrigoraAccountContext.Provider
      value={{
        userData,
        setUserData,
        isSubscribed,
        subscriptionPlan,
        subscriptionLimits,
        subscriptionAddons,
        whitelabelPurchaseShouldDisplay,
        isDev,
        isAIAllowed,
        isMailAllowed,
      }}
    >
      <Modal
        isOpened={isAppsumoModalVisible}
        setOpened={setIsAppsumoModalVisible}
        icon={<AppsumoIcon className="w-[100px] h-[100px] mx-auto" />}
        heading={
          <Trans id="grigoraAccount.appsumoLicenseActivated">
            {"Appsumo License Activated"}
          </Trans>
        }
        content={
          <p className="mb-5">
            <Trans id="grigoraAccount.appsumoActivatedMessage">
              {
                "Hello Sumo-ling! 👋 Your Appsumo license has been successfully activated. Enjoy your Grigora experience!"
              }
            </Trans>
          </p>
        }
      />
      {isLoading ? (
        <div className="w-full h-screen flex items-center justify-center">
          <PulseLoader color="black" size={6} />
        </div>
      ) : (
        <div className="flex flex-col h-screen overflow-y-auto">
          <BannerContainer userData={userData} />
          {props.children}
        </div>
      )}
    </GrigoraAccountContext.Provider>
  );
};
export { GrigoraAccount, GrigoraAccountContext };
